import React, { MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import {
    DossierOpenSearch,
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    NestedDossierFormaliteOpenSearch,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { ColumnDatatable } from '@europrocurement/flexy-datatable';
import { Box, Tooltip, Typography } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { USER_DEFAULT_FORMALISTE, useUserContactCard } from '../../Utilisateur';
import {
    PreviewColumn,
    DossierReferences,
    DossierCompanyFormality,
    PeoplePreview,
    DossierStepCompletion,
    DossierDateCreation,
    DossierNotifications,
    DossierStatus,
} from '../components/liste';

const useDossierTable = () => {
    const dispatch = useDispatch();
    const { getOpenSearchDossiers } = useFormaliteService();
    const { callUserContactCardModal } = useUserContactCard();

    const filterByCollectionId = (collectionId: number) => {
        setTimeout(() => {
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainPagination`,
                payload: {
                    page: 1,
                    itemsPerPage: 10,
                },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'collectionId',
                    value: [collectionId],
                },
            });
            dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
        }, 1000);
    };

    const columns: ColumnDatatable<DossierOpenSearch>[] = [
        {
            label: 'Mises à jour',
            render: ({
                contactPrenom,
                contactNom,
                contactUserIdentifier,
                societeRef,
                formalites,
                numero,
                documentsCount,
            }) => (
                <PreviewColumn
                    contactPrenom={contactPrenom}
                    contactNom={contactNom}
                    contactUserIdentifier={contactUserIdentifier}
                    formalites={formalites?.map(
                        (formalite) =>
                            ({
                                id: formalite?.id as number,
                                libelle: formalite?.libelle as string,
                            }) as NestedDossierFormaliteOpenSearch,
                    )}
                    societeRef={societeRef}
                    numero={numero}
                    documentsCount={documentsCount}
                />
            ),
            isDisplayed: true,
            sx: {
                display: {
                    xs: 'table-cell',
                    sm: 'table-cell',
                    md: 'none',
                    lg: 'none',
                    xl: 'none',
                },
            },
        },
        {
            label: 'Offre',
            render: ({ offreLibelle, offreIcon }) =>
                offreLibelle ? (
                    <Tooltip title={offreLibelle}>
                        <Box>
                            <FaOptionIcon icon={['fasl', offreIcon || 'star'] as IconProp} />
                        </Box>
                    </Tooltip>
                ) : null,
            isDisplayed: true,
            sortable: true,
            field: 'offreLibelle',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Références dossiers',
            render: ({ societeRef, numero, collectionNumero, collectionId, siblingCount }) => {
                const canFilter =
                    collectionId !== undefined &&
                    siblingCount !== undefined &&
                    siblingCount !== null &&
                    siblingCount > 1;

                const handleClick = (event: MouseEvent<HTMLSpanElement>) => {
                    if (!canFilter) {
                        return;
                    }

                    event.stopPropagation();
                    event.preventDefault();

                    filterByCollectionId(collectionId as number);
                };

                return (
                    <DossierReferences
                        itemReference={numero as string}
                        companyReference={societeRef as string}
                        collectionReference={collectionNumero as string}
                        onCollectionNumberClick={handleClick}
                        canFilterOnCollection={canFilter}
                    />
                );
            },
            isDisplayed: true,
            sortable: true,
            field: 'numero',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Sociétés',
            render: ({ societeDenomination, formalites = [] }) => (
                <DossierCompanyFormality
                    libelle={societeDenomination as string}
                    formalites={formalites?.map(
                        (formalite) =>
                            ({
                                id: formalite?.id as number,
                                libelle: formalite?.libelle as string,
                            }) as NestedDossierFormaliteOpenSearch,
                    )}
                />
            ),
            isDisplayed: true,
            sortable: true,
            field: 'societeDenomination',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Greffe',
            render: ({ greffeLibelle }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {greffeLibelle || 'Aucun'}
                    </Typography>
                </Box>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'greffeLibelle',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
            headerTooltip: 'Greffe rattaché au dossier',
        },
        {
            label: 'Col...',
            render: ({ contactNom, contactPrenom, contactUserIdentifier }) => (
                <PeoplePreview
                    firstName={contactPrenom as string}
                    lastName={contactNom as string}
                    userIdentifier={contactUserIdentifier as string}
                />
            ),
            isDisplayed: false,
            sortable: true,
            field: 'contactNom',
            sx: {
                width: '64px',

                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
            headerTooltip: 'Collaborateur',
        },
        {
            label: 'Étapes',
            render: ({ stepLabel, stepCompletion }) => (
                <DossierStepCompletion
                    step={stepLabel as string}
                    currentCompletion={stepCompletion as number}
                />
            ),
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Dates',
            render: ({ dateCreation }) => {
                if (!dateCreation) {
                    return null;
                }

                return <DossierDateCreation date={new Date(dateCreation)} />;
            },
            isDisplayed: true,
            sortable: true,
            field: 'dateCreation',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Notifications',
            render: ({ missingRequiredDocumentsCount }) => (
                <DossierNotifications documentsCount={missingRequiredDocumentsCount as number} />
            ),
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Statut',
            render: ({ statutLibelle, statutId }) => (
                <DossierStatus
                    statutId={statutId as number}
                    label={statutLibelle as string}
                />
            ),
            isDisplayed: true,
            sortable: true,
            field: 'statutLibelle',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Formaliste',
            render: ({
                formalisteId,
                formalisteNom,
                formalistePrenom,
                formalisteUserIdentifier,
                prescripteurId,
            }) => {
                if (formalisteId === USER_DEFAULT_FORMALISTE) {
                    return '';
                }

                return (
                    <PeoplePreview
                        firstName={formalistePrenom as string}
                        lastName={formalisteNom as string}
                        userIdentifier={formalisteUserIdentifier as string}
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                        onClick={(e: React.MouseEvent) => {
                            if (formalisteId) {
                                e.preventDefault();
                                e.stopPropagation();
                                callUserContactCardModal({
                                    userId: formalisteId as number,
                                    displayActionContact: true,
                                    displayUserSchedule: true,
                                    source: 'BACK',
                                    userType: 'UTILISATEUR',
                                    mailSubject: "Demande d'information",
                                    tiersIds: [prescripteurId as number],
                                });
                            }
                        }}
                    />
                );
            },
            isDisplayed: true,
            sortable: true,
            headerTooltip: 'Formaliste',
            field: 'formaliste',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
    ];

    return { columns };
};

export default useDossierTable;
